import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import customerApi, { CUSCustomer } from '../../../services/customerApi';

type Mode = 'browse' | 'add' | 'edit';

export function useCustomerForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<CUSCustomer[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Partial<CUSCustomer>>({});

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const detail = await customerApi.get(id);
      setCurrentRecord(detail);
    } catch {
      message.error('載入明細失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      const rec = records[currentIdx];
      loadDetail(rec.cus_mid);
    }
  }, [records, currentIdx, loadDetail]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setCurrentRecord({
      cus_code: '', cus_cname: '', cus_ename: '', comp_type: 'C',
      country_code: '', invoice: '', cus_prop: '', main_prop: 'T1',
      is_active: true, isvalid: false, ref_cus: false,
      contacts: [], prd_items: [], prices: [],
      color_stand: null, color_paras: [], color_quality: [],
      order_principle: null, samples: [],
    });
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      loadDetail(records[currentIdx].cus_mid);
    }
  }, [records, currentIdx, loadDetail]);

  const updateField = useCallback((field: string, value: any) => {
    setCurrentRecord((prev) => ({ ...prev, [field]: value }));
  }, []);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'add') {
        await customerApi.create(currentRecord);
        message.success('新增成功');
      } else {
        await customerApi.update(currentRecord.cus_mid!, currentRecord);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        } else if (err.response.data?.error) {
          message.error(err.response.data.error);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, currentRecord, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!currentRecord.cus_mid) return;
    setLoading(true);
    try {
      await customerApi.delete(currentRecord.cus_mid);
      message.success('刪除成功');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.data?.error) {
        message.error(err.response.data.error);
      } else {
        message.error('刪除失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [currentRecord.cus_mid, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) =>
      r.cus_code.toUpperCase().includes(keyword.toUpperCase()) ||
      r.cus_cname.includes(keyword)
    );
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx, currentRecord,
    updateField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
